import type * as React from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface RecordFormProps {
  children: React.ReactNode
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  onCancel?: () => void
  submitLabel?: string
  cancelLabel?: string
  processing?: boolean
  disabled?: boolean
  className?: string
  /* Negative margins that bleed the sticky footer over the page wrapper's
     padding so it sits flush with the viewport edge. Defaults to canceling a
     p-4 wrapper (admin pages); pages under a differently-padded layout can
     override (e.g. the partner layout's p-6 lg:p-10). */
  footerBleedClassName?: string
}

export default function RecordForm({
  children,
  onSubmit,
  onCancel,
  submitLabel = "Create",
  cancelLabel = "Cancel",
  processing = false,
  disabled = false,
  className,
  footerBleedClassName = "-mx-4 -mb-4",
}: RecordFormProps) {
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        {/* pb keeps the last field clear of the sticky footer. */}
        <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 space-y-6 overflow-y-auto pb-10">
          {children}
        </div>

        {/* Sticky so Cancel/Save stay on screen while the form scrolls, like
            the receptionist setup wizard. footerBleedClassName bleeds the bar
            over the page wrapper's padding so it sits flush with the edge. */}
        <div
          className={cn(
            "bg-background sticky bottom-0 z-10 mt-auto flex justify-end gap-3 border-t p-4",
            footerBleedClassName,
          )}
        >
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={processing}
            >
              {cancelLabel}
            </Button>
          )}
          {onSubmit && (
            <Button
              type="submit"
              disabled={disabled || processing}
              loading={processing}
            >
              {submitLabel}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
